import React, { useEffect, useRef, useState, useCallback } from 'react';
import WordReveal from '../shared/WordReveal';
import './MobileFeed.css';

// Smooth text reveal animation - reveals text character by character
// Optional renderFn applies formatting (like name highlighting) to the displayed text
function SmoothTextReveal({ text, speed = 15, renderFn }) {
  const [displayedLength, setDisplayedLength] = useState(0);
  const targetLengthRef = useRef(0);
  const animationRef = useRef(null);

  useEffect(() => {
    targetLengthRef.current = text.length;

    // Animate towards target length
    const animate = () => {
      setDisplayedLength(prev => {
        if (prev >= targetLengthRef.current) {
          return prev;
        }
        // Reveal multiple characters per frame for faster text
        const charsToAdd = Math.max(1, Math.ceil((targetLengthRef.current - prev) / 20));
        return Math.min(prev + charsToAdd, targetLengthRef.current);
      });
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [text]);

  const displayedText = text.slice(0, displayedLength);
  const isComplete = displayedLength >= text.length;
  const content = renderFn ? renderFn(displayedText) : displayedText;

  return (
    <div className="smooth-text-reveal">
      {content}
      {!isComplete && <span className="streaming-cursor"></span>}
    </div>
  );
}

function MobileFeed({ entries, loading, donors, streamingText, streamingEntryId, onNameTap, onGroupSelect, onSkipGroupSelect, onSaveAsList, onViewList, onCreateListForGroup, onEntryRef, onConfirmAdd, onDeclineAdd, onResearchPerson, onSkipResearch, onAcceptResearch, onDeclineResearch, onResearchOptionSelect, onCreateGroup, onScrollingUp, onHideNav, onProvideMoreInfo, onSkipMoreInfo }) {
  const feedEndRef = useRef(null);
  const feedContainerRef = useRef(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const isInitialLoad = useRef(true);
  const prevEntriesLength = useRef(0);
  const lastScrollTop = useRef(0);
  const scrollTimeout = useRef(null);

  // Word reveal animation tracking
  const [animatingEntryId, setAnimatingEntryId] = useState(null);
  const prevStreamingEntryIdRef = useRef(null);

  // Detect when streaming ends and trigger word reveal animation
  useEffect(() => {
    // If we were streaming an entry and now we're not, animate it
    if (prevStreamingEntryIdRef.current && !streamingEntryId) {
      const entryToAnimate = prevStreamingEntryIdRef.current;
      setAnimatingEntryId(entryToAnimate);

      // Find the entry to calculate animation duration
      const entry = entries.find(e => e.id === entryToAnimate);
      const message = entry?.message || '';
      const wordCount = message.split(/\s+/).filter(w => w.trim()).length;

      // Clear animation after it completes (50ms per word + buffer)
      setTimeout(() => setAnimatingEntryId(null), wordCount * 50 + 500);
    }
    prevStreamingEntryIdRef.current = streamingEntryId;
  }, [streamingEntryId, entries]);

  // Generate consistent color for a name (cool colors only)
  const getAvatarColor = (name) => {
    const colors = ['#a78bfa', '#60a5fa', '#34d399', '#2dd4bf', '#818cf8', '#22d3d1'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Get initials from name
  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Render markdown-style formatting into React elements
  const renderMarkdown = (text, applyHighlights = null) => {
    if (!text) return null;

    // Split into lines for block-level parsing
    const lines = text.split('\n');
    const elements = [];
    let currentList = null;
    let currentListType = null;
    let listKey = 0;

    const flushList = () => {
      if (currentList && currentList.length > 0) {
        if (currentListType === 'bullet') {
          elements.push(
            <ul key={`list-${listKey++}`} className="md-list">
              {currentList.map((item, i) => (
                <li key={i}>{renderInlineMarkdown(item, applyHighlights)}</li>
              ))}
            </ul>
          );
        } else {
          elements.push(
            <ol key={`list-${listKey++}`} className="md-list">
              {currentList.map((item, i) => (
                <li key={i}>{renderInlineMarkdown(item, applyHighlights)}</li>
              ))}
            </ol>
          );
        }
        currentList = null;
        currentListType = null;
      }
    };

    lines.forEach((line, lineIdx) => {
      const trimmed = line.trim();

      // Empty line - flush any list and add spacing
      if (!trimmed) {
        flushList();
        return;
      }

      // Headers: ## Header or ### Header
      const headerMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
      if (headerMatch) {
        flushList();
        const level = headerMatch[1].length;
        const headerText = headerMatch[2];
        elements.push(
          <div key={lineIdx} className={`md-header md-h${level}`}>
            {renderInlineMarkdown(headerText, applyHighlights)}
          </div>
        );
        return;
      }

      // Bullet list: - item or • item or * item
      const bulletMatch = trimmed.match(/^[-•*]\s+(.+)$/);
      if (bulletMatch) {
        if (currentListType !== 'bullet') {
          flushList();
          currentList = [];
          currentListType = 'bullet';
        }
        currentList.push(bulletMatch[1]);
        return;
      }

      // Numbered list: 1. item or 1) item
      const numberedMatch = trimmed.match(/^\d+[.)]\s+(.+)$/);
      if (numberedMatch) {
        if (currentListType !== 'numbered') {
          flushList();
          currentList = [];
          currentListType = 'numbered';
        }
        currentList.push(numberedMatch[1]);
        return;
      }

      // Regular paragraph
      flushList();
      elements.push(
        <p key={lineIdx} className="md-paragraph">
          {renderInlineMarkdown(trimmed, applyHighlights)}
        </p>
      );
    });

    // Flush any remaining list
    flushList();

    return <div className="md-content">{elements}</div>;
  };

  // Render inline markdown (bold, italic) and apply highlights
  const renderInlineMarkdown = (text, applyHighlights) => {
    if (!text) return null;

    // Pattern to match **bold** and *italic*
    const parts = [];
    let remaining = text;
    let key = 0;

    while (remaining) {
      // Look for **bold**
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      // Look for *italic* (but not **)
      const italicMatch = remaining.match(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/);

      let firstMatch = null;
      let matchType = null;

      if (boldMatch && (!italicMatch || boldMatch.index <= italicMatch.index)) {
        firstMatch = boldMatch;
        matchType = 'bold';
      } else if (italicMatch) {
        firstMatch = italicMatch;
        matchType = 'italic';
      }

      if (firstMatch) {
        // Add text before the match
        if (firstMatch.index > 0) {
          const beforeText = remaining.slice(0, firstMatch.index);
          parts.push(
            <span key={key++}>
              {applyHighlights ? applyHighlights(beforeText) : beforeText}
            </span>
          );
        }

        // Add the formatted text
        const innerText = firstMatch[1];
        if (matchType === 'bold') {
          parts.push(
            <strong key={key++}>
              {applyHighlights ? applyHighlights(innerText) : innerText}
            </strong>
          );
        } else {
          parts.push(
            <em key={key++}>
              {applyHighlights ? applyHighlights(innerText) : innerText}
            </em>
          );
        }

        remaining = remaining.slice(firstMatch.index + firstMatch[0].length);
      } else {
        // No more matches, add remaining text
        parts.push(
          <span key={key++}>
            {applyHighlights ? applyHighlights(remaining) : remaining}
          </span>
        );
        remaining = '';
      }
    }

    return parts.length > 0 ? parts : text;
  };

  // Check if scrolled near bottom and detect scroll direction
  const checkScrollPosition = useCallback(() => {
    const container = feedContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    // Show arrow if more than 100px from bottom
    setShowScrollDown(distanceFromBottom > 100);

    // Detect scroll direction for input bar collapse and nav hiding
    // Require minimum 15px scroll delta to prevent jitter
    const scrollDelta = scrollTop - lastScrollTop.current;
    const significantScroll = Math.abs(scrollDelta) > 15;

    if (significantScroll) {
      lastScrollTop.current = scrollTop;

      // Only compact/hide when scrolling up, not near bottom, and scrolled past top
      const isScrollingUp = scrollDelta < 0 && scrollTop > 50 && distanceFromBottom > 50;

      if (onScrollingUp) {
        onScrollingUp(isScrollingUp);

        // Reset to expanded after user stops scrolling
        if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
        scrollTimeout.current = setTimeout(() => {
          onScrollingUp(false);
        }, 200);
      }

      // Hide nav when scrolling up
      if (onHideNav) {
        onHideNav(isScrollingUp);
      }
    }
  }, [onScrollingUp, onHideNav]);

  // Scroll to bottom (smooth or instant)
  const scrollToBottom = useCallback((smooth = true) => {
    if (feedEndRef.current) {
      feedEndRef.current.scrollIntoView({ behavior: smooth ? 'smooth' : 'instant' });
    }
  }, []);

  // Scroll to bottom when new entries are added or content updates
  useEffect(() => {
    if (entries.length === 0) return;

    if (isInitialLoad.current) {
      // First load - jump to bottom instantly (no animation)
      isInitialLoad.current = false;
      prevEntriesLength.current = entries.length;
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => scrollToBottom(false), 0);
    } else if (entries.length > prevEntriesLength.current) {
      // New entries added - smooth scroll
      prevEntriesLength.current = entries.length;
      scrollToBottom(true);
    }
  }, [entries, scrollToBottom]);

  // Also scroll when the most recent entry updates (AI response comes in)
  const latestEntry = entries[0];
  useEffect(() => {
    if (latestEntry?.status === 'received' || latestEntry?.askResearch || latestEntry?.askMoreInfo) {
      // Delay slightly to let DOM render the full content
      setTimeout(() => scrollToBottom(true), 100);
    }
  }, [latestEntry?.status, latestEntry?.message, latestEntry?.askResearch, latestEntry?.askMoreInfo, scrollToBottom]);

  // Scroll during streaming
  useEffect(() => {
    if (streamingText) {
      scrollToBottom(false);
    }
  }, [streamingText, scrollToBottom]);

  // Add scroll listener
  useEffect(() => {
    const container = feedContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', checkScrollPosition);
    return () => container.removeEventListener('scroll', checkScrollPosition);
  }, [checkScrollPosition]);

  // Get day label for grouping
  const getDayLabel = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / 86400000);

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  };

  // Get all known donor names for highlighting
  const donorNames = Object.keys(donors || {});

  // Interest keywords to highlight
  const interestPatterns = [
    'art', 'arts', 'artist', 'artistic',
    'film', 'films', 'filmmaker', 'cinema', 'animation', 'animated',
    'music', 'musician', 'musical',
    'theater', 'theatre', 'dance', 'ballet', 'opera',
    'photography', 'sculptor', 'sculpture', 'painter', 'painting',
    'philanthropy', 'philanthropic', 'charity', 'charitable', 'nonprofit', 'non-profit',
    'education', 'educational', 'scholarship', 'scholarships',
    'technology', 'tech', 'startup', 'startups', 'innovation',
    'healthcare', 'health', 'medicine', 'medical',
    'environment', 'environmental', 'climate', 'sustainability', 'sustainable',
    'social impact', 'social justice', 'advocacy',
    'real estate', 'finance', 'banking', 'investment', 'investments',
    'media', 'journalism', 'publishing',
    'fashion', 'design', 'architecture',
    'sports', 'athletics', 'fitness',
    'food', 'culinary', 'restaurant', 'restaurants',
    'travel', 'hospitality', 'wine', 'wines'
  ];

  // Sanitize messages to strip raw JSON that may leak through on connection errors
  const sanitizeMessage = (msg) => {
    if (!msg || typeof msg !== 'string') return msg;
    // Remove JSON objects that look like AI action responses
    return msg
      .replace(/\{"type":\s*"[^"]*",\s*"data":\s*\{[^}]*\}\}/g, '')
      .replace(/\{"type":\s*"[^"]*",\s*"data":\s*\{[^}]*"[^}]*\}\}/g, '')
      .replace(/\{[^{}]*"type":\s*"(update_person|add_person|open_add_modal|show_person|clarify_person)"[^{}]*\}/g, '')
      .trim() || 'Done.';
  };

  // Highlight names, amounts, and interests in message text
  const renderMessageWithNames = (message, primaryDonor = null) => {
    if (!message) return message;

    // Sanitize first to remove any raw JSON
    const cleanMessage = sanitizeMessage(message);
    if (!cleanMessage) return cleanMessage;

    // Build regex pattern for all donor names (full names and first names)
    const namePatterns = [];
    const nameMap = {}; // Map lowercase pattern to actual donor name

    donorNames.forEach(fullName => {
      const escapedFull = fullName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      namePatterns.push(escapedFull);
      nameMap[fullName.toLowerCase()] = fullName;

      // Also match first name if it's distinct enough (3+ chars)
      const firstName = fullName.split(' ')[0];
      if (firstName.length >= 3) {
        const escapedFirst = firstName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        namePatterns.push(escapedFirst);
        nameMap[firstName.toLowerCase()] = fullName;
      }
    });

    // Sort by length descending to match longer names first
    namePatterns.sort((a, b) => b.length - a.length);

    // Amount pattern: $X, large numbers like 20,000 or 50k
    const amountPattern = '\\$[\\d,]+[kKmM]?|\\b\\d{1,3}(?:,\\d{3})+(?:[kKmM])?\\b|\\b\\d+[kKmM]\\b';

    // Interest pattern
    const interestPattern = '\\b(?:' + interestPatterns.join('|') + ')\\b';

    // Build combined pattern
    const allPatternsArray = [amountPattern];
    if (namePatterns.length > 0) {
      allPatternsArray.push('\\b(?:' + namePatterns.join('|') + ')\\b');
    }
    allPatternsArray.push(interestPattern);

    const combinedPattern = '(' + allPatternsArray.join('|') + ')';
    const pattern = new RegExp(combinedPattern, 'gi');
    const parts = cleanMessage.split(pattern);

    // Helper to check pattern matches
    const isAmount = (str) => /^(\$[\d,]+[kKmM]?|\d{1,3}(?:,\d{3})+[kKmM]?|\d+[kKmM])$/i.test(str);
    const isInterest = (str) => new RegExp('^(' + interestPattern + ')$', 'i').test(str);

    return parts.map((part, i) => {
      if (!part) return null;

      // Check if it's an amount
      if (isAmount(part)) {
        return (
          <span key={i} className="feed-amount-highlight">
            {part}
          </span>
        );
      }

      // Check if it's a donor name
      const matchedName = nameMap[part.toLowerCase()];
      if (matchedName) {
        return (
          <span
            key={i}
            className="feed-name-highlight"
            onClick={(e) => {
              e.stopPropagation();
              onNameTap(matchedName);
            }}
          >
            {part}
          </span>
        );
      }

      // Check if it's an interest
      if (isInterest(part)) {
        return (
          <span key={i} className="feed-interest-highlight">
            {part}
          </span>
        );
      }

      return part;
    });
  };


  // Render rich AI response with sections and people cards
  const renderRichResponse = (entry) => {
    const { message, sections, people } = entry;
    const isThinking = entry.status === 'thinking';
    const isStreamingThis = streamingEntryId === entry.id && streamingText;

    // Show streaming text with formatting (key forces React to re-render on each update)
    if (isStreamingThis) {
      return (
        <div className="feed-entry-message streaming-text" key={`streaming-${streamingText.length}`}>
          {renderMarkdown(streamingText, renderMessageWithNames)}
          <span className="streaming-cursor" />
        </div>
      );
    }

    // Show thinking dots only when no message yet and not streaming
    if (isThinking && !message) {
      return (
        <div className="feed-thinking">
          <span className="thinking-dot"></span>
          <span className="thinking-dot"></span>
          <span className="thinking-dot"></span>
        </div>
      );
    }

    return (
      <>
        {/* Quick brief section */}
        {sections?.brief && (
          <div className="response-section">
            <div className="response-section-label">Quick brief</div>
            <div className="response-section-content">
              {animatingEntryId === entry.id ? (
                <WordReveal animate={true} speed={40}>
                  {renderMessageWithNames(sections.brief)}
                </WordReveal>
              ) : (
                renderMessageWithNames(sections.brief)
              )}
            </div>
          </div>
        )}

        {/* Main message */}
        {!sections?.brief && message && (
          <div className="feed-entry-message">
            {animatingEntryId === entry.id ? (
              <WordReveal animate={true} speed={40}>
                {renderMarkdown(message, renderMessageWithNames)}
              </WordReveal>
            ) : (
              renderMarkdown(message, renderMessageWithNames)
            )}
          </div>
        )}

        {/* Talking points section */}
        {sections?.talkingPoints && sections.talkingPoints.length > 0 && (
          <div className="response-section">
            <div className="response-section-label">Talking points</div>
            <div className="response-talking-points">
              {sections.talkingPoints.map((point, i) => (
                <div key={i} className="talking-point">• {renderMessageWithNames(point)}</div>
              ))}
            </div>
          </div>
        )}

        {/* People cards */}
        {people && people.length > 0 && (
          <div className="response-people">
            {people.map((person, i) => (
              <div
                key={i}
                className="response-person-card"
                onClick={(e) => {
                  e.stopPropagation();
                  onNameTap(person.name);
                }}
              >
                <div
                  className="person-card-avatar"
                  style={{ borderColor: getAvatarColor(person.name), color: getAvatarColor(person.name) }}
                >
                  {getInitials(person.name)}
                </div>
                <div className="person-card-info">
                  <div className="person-card-name">
                    {person.name}
                    {person.isNew && <span className="person-card-badge new">New</span>}
                    {person.urgent && <span className="person-card-badge urgent">Late</span>}
                    {person.badge && <span className="person-card-badge">{person.badge}</span>}
                  </div>
                  {person.reason && (
                    <div className="person-card-reason">{person.reason}</div>
                  )}
                </div>
                <span className="person-card-arrow">›</span>
              </div>
            ))}
          </div>
        )}

        {/* Source info */}
        {entry.sourceInfo && (
          <div className="response-source">{entry.sourceInfo}</div>
        )}

        {/* Suggest add confirmation */}
        {entry.suggestAdd && (
          <div className="suggest-add-buttons">
            <button
              className="suggest-add-btn confirm"
              onClick={(e) => {
                e.stopPropagation();
                onConfirmAdd && onConfirmAdd(entry.id, entry.suggestAdd.name, entry.suggestAdd.notes);
              }}
            >
              Yes, add {entry.suggestAdd.name.split(' ')[0]}
            </button>
            <button
              className="suggest-add-btn decline"
              onClick={(e) => {
                e.stopPropagation();
                onDeclineAdd && onDeclineAdd(entry.id);
              }}
            >
              No thanks
            </button>
          </div>
        )}

        {/* Group selection for newly added donors */}
        {entry.groupSelection && (
          <div className="group-selection">
            <div className="group-selection-label">Add to a group?</div>
            <div className="group-selection-options">
              {entry.groupSelection.groups.map((group) => (
                <button
                  key={group.id}
                  className="group-option"
                  onClick={(e) => {
                    e.stopPropagation();
                    onGroupSelect(
                      entry.id,
                      entry.groupSelection.donorId,
                      entry.groupSelection.donorName,
                      group.id,
                      group.name
                    );
                  }}
                >
                  <span
                    className="group-option-dot"
                    style={{ background: group.color || '#a78bfa' }}
                  />
                  <span className="group-option-name">{group.name}</span>
                </button>
              ))}
              <button
                className="group-option group-option-new"
                onClick={(e) => {
                  e.stopPropagation();
                  onGroupSelect(
                    entry.id,
                    entry.groupSelection.donorId,
                    entry.groupSelection.donorName,
                    'new',
                    null
                  );
                }}
              >
                <span className="group-option-plus">+</span>
                <span className="group-option-name">New group</span>
              </button>
              <button
                className="group-option group-option-skip"
                onClick={(e) => {
                  e.stopPropagation();
                  onSkipGroupSelect(entry.id, entry.groupSelection.donorName, entry.groupSelection.donorId);
                }}
              >
                <span className="group-option-name">Skip</span>
              </button>
            </div>
          </div>
        )}

        {/* Prompt to create a group (for new users) */}
        {entry.promptCreateGroup && (
          <div className="group-selection">
            <div className="group-selection-label">Create a group for {entry.promptCreateGroup.forPerson || 'your contacts'}?</div>
            <div className="group-selection-options">
              <button
                className="group-option group-option-new"
                onClick={(e) => {
                  e.stopPropagation();
                  onCreateGroup && onCreateGroup(
                    entry.id,
                    entry.promptCreateGroup.suggestedName,
                    entry.promptCreateGroup.forPerson
                  );
                }}
              >
                <span className="group-option-plus">+</span>
                <span className="group-option-name">Create "{entry.promptCreateGroup.suggestedName}"</span>
              </button>
              <button
                className="group-option"
                onClick={(e) => {
                  e.stopPropagation();
                  onCreateGroup && onCreateGroup(entry.id, null, entry.promptCreateGroup.forPerson);
                }}
              >
                <span className="group-option-name">Name it myself</span>
              </button>
              <button
                className="group-option group-option-skip"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeclineAdd && onDeclineAdd(entry.id);
                }}
              >
                <span className="group-option-name">Skip for now</span>
              </button>
            </div>
          </div>
        )}

        {/* Research prompt after adding */}
        {entry.askResearch && (
          <>
            <div className="feed-entry-message">
              Would you like me to research {entry.askResearch.name.split(' ')[0]}?
            </div>
            <div className="suggest-add-buttons">
              <button
                className="suggest-add-btn confirm"
                onClick={(e) => {
                  e.stopPropagation();
                  onResearchPerson && onResearchPerson(entry.id, entry.askResearch.name);
                }}
              >
                Research
              </button>
              <button
                className="suggest-add-btn decline"
                onClick={(e) => {
                  e.stopPropagation();
                  // Show their profile card and dismiss
                  onSkipResearch && onSkipResearch(entry.id, entry.askResearch.name, entry.askResearch.donorId);
                }}
              >
                Skip
              </button>
            </div>
          </>
        )}

        {/* Donor card - clickable to open profile */}
        {entry.donorCard && (
          <div
            className="donor-card-link"
            onClick={(e) => {
              e.stopPropagation();
              onNameTap && onNameTap(entry.donorCard.name);
            }}
          >
            <div className="donor-card-avatar">
              {entry.donorCard.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
            </div>
            <span className="donor-card-name">{entry.donorCard.name}</span>
            <span className="donor-card-arrow">›</span>
          </div>
        )}

        {/* Offer to research externally when AI can't answer from data */}
        {entry.offerResearch && (
          <div className="suggest-add-buttons">
            <button
              className="suggest-add-btn confirm"
              onClick={(e) => {
                e.stopPropagation();
                onAcceptResearch && onAcceptResearch(entry.id, entry.offerResearch.query, entry.offerResearch.context);
              }}
            >
              Yes, look it up
            </button>
            <button
              className="suggest-add-btn decline"
              onClick={(e) => {
                e.stopPropagation();
                onDeclineResearch && onDeclineResearch(entry.id);
              }}
            >
              No thanks
            </button>
          </div>
        )}

        {/* Research options when multiple people found with same name */}
        {entry.researchOptions && (
          <div className="research-options">
            {entry.researchOptions.options.map((option, i) => (
              <button
                key={i}
                className="research-option"
                onClick={(e) => {
                  e.stopPropagation();
                  onResearchOptionSelect && onResearchOptionSelect(
                    entry.id,
                    entry.researchOptions.donorName,
                    entry.researchOptions.donorId,
                    option
                  );
                }}
              >
                <span className="research-option-name">{option.name}</span>
                <span className="research-option-desc">{option.description}</span>
              </button>
            ))}
          </div>
        )}

        {/* Group selection for list creation */}
        {entry.listGroupSelection && (
          <div className="list-group-selection">
            <div className="list-group-prompt">Which group would you like me to organize?</div>
            <div className="list-group-buttons">
              {entry.listGroupSelection.groups.map((group) => (
                <button
                  key={group.id}
                  className="list-group-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateListForGroup && onCreateListForGroup(
                      entry.id,
                      group,
                      entry.listGroupSelection.listPrompt,
                      entry.listGroupSelection.suggestedName
                    );
                  }}
                >
                  <div
                    className="list-group-dot"
                    style={{ background: group.color || '#a78bfa' }}
                  />
                  {group.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* List preview when a list was created */}
        {entry.listPreview && (
          <div className="list-preview">
            <div className="list-preview-groups">
              {entry.listPreview.groups.map((group, i) => (
                <div key={i} className="list-preview-group">
                  <div
                    className="list-preview-dot"
                    style={{ background: group.color || '#a78bfa' }}
                  />
                  <span className="list-preview-name">{group.name}</span>
                  <span className="list-preview-count">{group.people?.length || 0}</span>
                </div>
              ))}
            </div>
            <div className="list-preview-actions">
              <button
                className="list-preview-btn primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onViewList && onViewList(entry.listPreview);
                }}
              >
                View List
              </button>
              <button
                className="list-preview-btn secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  // TODO: Implement adjust groups
                }}
              >
                Adjust Groups
              </button>
            </div>
          </div>
        )}

        {/* Save as list button - only show when response has multiple people (a real list) */}
        {people && people.length >= 3 && entry.status === 'received' && onSaveAsList && (
          <button
            className="save-as-list-button"
            onClick={(e) => {
              e.stopPropagation();
              onSaveAsList(entry);
            }}
          >
            <span className="save-as-list-icon">+</span>
            Save as list
          </button>
        )}

        {/* Captured items confirmations */}
        {entry.captured && entry.captured.length > 0 && (
          <div className="feed-entry-captured">
            {entry.captured.map((item, i) => (
              <span
                key={i}
                className="captured-item"
                style={{ color: getCapturedColor(item.type) }}
              >
                {getCapturedIcon(item.type)} {item.text}
              </span>
            ))}
          </div>
        )}
      </>
    );
  };

  // Get icon for captured item type
  const getCapturedIcon = (type) => {
    switch (type) {
      case 'meeting': return '✓';
      case 'amount': return '✓';
      case 'task': return '→';
      case 'logged': return '✓';
      case 'error': return '✕';
      default: return '✓';
    }
  };

  // Get color for captured item type
  const getCapturedColor = (type) => {
    switch (type) {
      case 'meeting': return '#4ade80';
      case 'amount': return '#4ade80';
      case 'task': return '#fbbf24';
      case 'logged': return '#4ade80';
      case 'error': return '#ef4444';
      default: return '#4ade80';
    }
  };

  // Reverse entries for chat-style (oldest at top, newest at bottom)
  const reversedEntries = [...entries].reverse();

  // Group entries by day (with reversed order)
  const entriesByDay = reversedEntries.reduce((acc, entry) => {
    const day = getDayLabel(entry.timestamp);
    if (!acc[day]) acc[day] = [];
    acc[day].push(entry);
    return acc;
  }, {});

  // Sort days chronologically (oldest first)
  const sortedDays = Object.keys(entriesByDay).sort((a, b) => {
    const dateA = new Date(entriesByDay[a][0]?.timestamp || 0);
    const dateB = new Date(entriesByDay[b][0]?.timestamp || 0);
    return dateA - dateB;
  });

  return (
    <div className="mobile-feed">
      {/* Scroll to bottom button */}
      {showScrollDown && (
        <button className="scroll-down-btn" onClick={() => scrollToBottom(true)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
      )}

      {/* Feed entries */}
      <div className="feed-entries" ref={feedContainerRef}>
        {!loading && entries.length === 0 ? (
          <>
            <div className="feed-spacer" />
            <div className="feed-entry feed-answer">
              <div className="feed-entry-message">Tell me about someone.</div>
            </div>
          </>
        ) : (
          <>
            {/* Spacer pushes content to bottom */}
            <div className="feed-spacer" />
            {sortedDays.map((day) => (
              <div key={day} className="feed-day-group">
                <div className="feed-day-label">{day}</div>
                {entriesByDay[day].map((entry) => {
                  // Question entry (user asked something)
                  if (entry.type === 'question') {
                    return (
                      <div
                        key={entry.id}
                        ref={el => onEntryRef?.(entry.id, el)}
                        className="feed-entry feed-question"
                      >
                        <div className="feed-entry-message">
                          {renderMessageWithNames(entry.message, entry.donorName)}
                        </div>
                      </div>
                    );
                  }

                  // Answer entry (AI response)
                  if (entry.type === 'answer') {
                    return (
                      <div
                        key={entry.id}
                        ref={el => onEntryRef?.(entry.id, el)}
                        className={`feed-entry feed-answer ${entry.status === 'streaming' ? 'streaming' : ''}`}
                      >
                        {renderRichResponse(entry)}
                      </div>
                    );
                  }

                  // Note entry (default)
                  return (
                    <div
                      key={entry.id}
                      ref={el => onEntryRef?.(entry.id, el)}
                      className={`feed-entry feed-note ${entry.status === 'sending' ? 'sending' : ''}`}
                    >
                      {/* Message text */}
                      <div className="feed-entry-message">
                        {renderMessageWithNames(entry.message, entry.donorName)}
                      </div>

                      {/* Captured items */}
                      {entry.captured && entry.captured.length > 0 && (
                        <div className="feed-entry-captured">
                          {entry.captured.map((item, i) => (
                            <span
                              key={i}
                              className="captured-item"
                              style={{ color: getCapturedColor(item.type) }}
                            >
                              {getCapturedIcon(item.type)} {item.text}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Sending indicator */}
                      {entry.status === 'sending' && (
                        <div className="feed-entry-sending">Saving...</div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
            {/* Scroll anchor */}
            <div ref={feedEndRef} />
          </>
        )}
      </div>
    </div>
  );
}

export default MobileFeed;
